import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "./api";
import { useEntities } from "./entity";
import { useMe } from "./session";
import type { Impact, Nature, Transaction, Transfer } from "./api";
import { Modal } from "./components/Modal";
import { ImpactList, TxForm } from "./components/TxForm";
import { TransferForm } from "./components/TransferForm";
import { Btn } from "./components/ui";

interface Ctx {
  openNew: (kind: Nature) => void;
  openEdit: (t: Transaction) => void;
  /** Transferência entre contas (nova ou edição). */
  openTransfer: (t?: Transfer) => void;
  /** Mostra o impacto no saldo (a linha que gerou a ação pode já ter saído da lista). */
  showImpacts: (i: Impact[]) => void;
  /** Muda a cada gravação: as telas recarregam seus dados. */
  version: number;
  bump: () => void;
}
const C = createContext<Ctx | null>(null);
export const useTxFlow = () => {
  const c = useContext(C);
  if (!c) throw new Error("useTxFlow fora do TxFlowProvider");
  return c;
};

/** Formulário de lançamento acessível de qualquer tela (botão "+ Novo" do cabeçalho e ações das listas). */
export function TxFlowProvider({ children }: { children: ReactNode }) {
  const [form, setForm] = useState<{ kind: Nature; tx: Transaction | null } | null>(null);
  const [transfer, setTransfer] = useState<{ t: Transfer | null } | null>(null);
  const [version, setVersion] = useState(0);
  const [impacts, setImpacts] = useState<Impact[]>([]);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  const { me } = useMe();
  const { current } = useEntities();
  // lançamentos fixos: ao abrir o ambiente, renova (idempotente) as séries que estão perto do fim; só quem pode gravar
  useEffect(() => {
    if (!current || current.permission !== "total") return;
    void api<{ created: number }>("POST", `/api/${me.user.realm}/recurrences/renew`, { entityId: current.id }).then((r) => { if (r.created > 0) bump(); }).catch(() => undefined);
  }, [current?.id, current?.permission, me.user.realm, bump]);
  const value = useMemo<Ctx>(() => ({
    openNew: (kind) => setForm({ kind, tx: null }),
    openEdit: (tx) => setForm({ kind: tx.kind, tx }),
    openTransfer: (t) => setTransfer({ t: t ?? null }),
    showImpacts: (i) => { if (i.length) setImpacts(i); },
    version, bump,
  }), [version, bump]);
  return (
    <C.Provider value={value}>
      {children}
      {form && <TxForm key={form.tx?.id ?? "new-" + form.kind} kind={form.kind} tx={form.tx} onClose={() => { setForm(null); bump(); }} />}
      {transfer && <TransferForm key={transfer.t?.id ?? "new-transfer"} transfer={transfer.t} onClose={() => { setTransfer(null); bump(); }} />}
      {impacts.length > 0 && <Modal title="Impacto no saldo" onClose={() => setImpacts([])}><ImpactList impacts={impacts} /><div className="modal-actions"><Btn onClick={() => setImpacts([])}>Entendi</Btn></div></Modal>}
    </C.Provider>
  );
}
